import React, { useState } from 'react';
import styled from 'styled-components';
import { iRisk, RiskWithAssetInfo } from '../../../Global/riskReducer';
import { SortState, Table } from '../../../Reusables/table/Table';
import { Footer } from './Footer';
import { useHeader } from './Header';
import { Banner, Button, HStack, Primitive } from '@rtkwlf/fenrir-react';
import { RisksParams } from '../../../types/risks';
import RescanControl from '../../../Reusables/RescanControl';
import { useSelector } from 'react-redux';
import { infoPanSelectors } from '../../../InfoPanes/infoPaneReducer';
import { useRiskTableColumns } from './use-risk-table-columns';

const SELECTION_LIMIT = 1000;

const RiskUpdateControls = styled(HStack)`
  background-color: #eeeeee;
  border: 1px solid #cccccc;
  box-sizing: border-box;
`;

type Props = {
  riskArray: Array<RiskWithAssetInfo>;
  highlightList: Array<string>;
  nextPage: string;
  onSort: (column: string, sortState: SortState) => void;
  page: number | undefined;
  pageSize: number;
  rowClick: (riskId: string) => void;
  setPage: (page: number) => void;
  totalItems: number;
  selectAll: (checked: boolean) => void;
  selectRisk: (risk: iRisk, event: React.FormEvent<HTMLInputElement>) => void;
  clearAllSelected: () => void;
  updateRisks: () => void;
  onChange: <K extends keyof RisksParams>(
    key: K,
    value: RisksParams[K]
  ) => void;
  rescanBtnDisabled: boolean;
  rescanSelected: () => Promise<void>;
  rescanIsLoading: boolean;
};

const RiskTable = ({
  riskArray = [],
  highlightList,
  nextPage,
  onSort,
  page,
  pageSize,
  rowClick,
  setPage,
  totalItems,
  selectAll,
  selectRisk,
  clearAllSelected,
  updateRisks,
  onChange,
  rescanBtnDisabled,
  rescanSelected,
  rescanIsLoading,
}: Props) => {
  const { hiddenColumnKeys, header } = useHeader(pageSize, onChange);

  const [rescanModalVisibility, setRescanModalVisibility] = useState(false);

  const isRiskPaneOpen = useSelector(infoPanSelectors.getPaneIsOpen);
  const riskPanePayload = useSelector(infoPanSelectors.getSelectedRiskPayload);
  const paneRiskId =
    isRiskPaneOpen && riskPanePayload ? riskPanePayload['riskId'] : '';

  const selectedRisks = React.useMemo(() => {
    if (!highlightList.length) {
      return null;
    }

    return (
      <RiskUpdateControls
        gap='zero'
        alignItems='center'
        position='relative'
        marginTop='smedium'
        marginBottom='smedium'
        marginLeft='zero'
        marginRight='zero'
        padding='small'
        borderRadius='small'
        data-testid='selected-risk-count'
        xAlign='between'
      >
        <HStack gap='zero'>
          <Primitive.span
            marginLeft='smedium'
            marginRight='smedium'
            marginTop='zero'
          >{`${highlightList.length} item(s) selected`}</Primitive.span>
          <Button
            marginBottom='zero'
            data-testid='update-selected'
            onClick={() => updateRisks()}
            iconLeft={<i className='fa fa-refresh' />}
            isDisabled={highlightList.length > SELECTION_LIMIT}
          >
            Update Selected
          </Button>
          <RescanControl
            isRescanDisabled={rescanBtnDisabled}
            isModalOpen={rescanModalVisibility}
            isLoading={rescanIsLoading}
            modalTitle='Rescan Risks'
            modalMessage='Are you sure you want to rescan the selected risks?'
            handleRescanClick={() => setRescanModalVisibility(true)}
            handleModalConfirm={rescanSelected}
            handleModalCancel={() => setRescanModalVisibility(false)}
          />
        </HStack>
        <Button
          marginBottom='zero'
          variant='link'
          onClick={() => clearAllSelected()}
          iconRight={<i className='fa fa-times' />}
        >
          Deselect All
        </Button>
      </RiskUpdateControls>
    );
  }, [
    highlightList,
    clearAllSelected,
    updateRisks,
    rescanSelected,
    rescanModalVisibility,
    rescanBtnDisabled,
    rescanIsLoading,
  ]);

  const columns = useRiskTableColumns(
    riskArray,
    selectAll,
    highlightList,
    selectRisk,
    hiddenColumnKeys
  );

  return (
    <Primitive.div data-testid='Risk-table'>
      {header}
      {highlightList.length > SELECTION_LIMIT && (
        <Banner
          appearance='warning'
          description={`Selection limit exceeded. Bulk update may not exceed ${SELECTION_LIMIT} rows.`}
        />
      )}
      {selectedRisks}
      <div
        style={{
          overflowX: 'auto',
          paddingBottom: '94px',
          marginBottom: '-84px',
          marginTop: '-20px',
          paddingTop: '20px',
        }}
      >
        <Table
          data={riskArray}
          columns={columns}
          dataUniqueKey='id'
          onRow={{
            onClick: (e, risk) => {
              rowClick(risk.id);
            },
          }}
          onSort={onSort}
          emptyComponent={<div>No Data Available</div>}
          selectedRowKey={paneRiskId}
        />
      </div>
      <Footer
        page={page || 0}
        setPage={setPage}
        pageSize={pageSize}
        totalItems={totalItems}
        highlightList={highlightList}
        selectAll={selectAll}
      />
    </Primitive.div>
  );
};

export default RiskTable;
